import React, { useEffect, useMemo, useState } from 'react';
import { LEAccordionNames } from 'components/organisms/LEAccordions';
import { ERouteLinks } from 'models/route';
import { useTranslations } from 'hooks/useTranslations';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { useQuery } from 'hooks/useQuery';
import { useLang } from 'models/langContext';
import { ELegalEntityTypes } from 'constants/LegalEntityTypes';
import LicenceStore from 'state/LicenceStore';
import { inNextTick } from 'utils/stateUtils';
import { ILicenceFirearmsResponse } from 'models/licencesFirearms';
import { useRenewLicence } from './hooks';
import { LicenceRenewEdit } from './licenceRenewEdit';
import { useLegalEntities } from '../hooks';
import { Elabels, getBreadcrumbsBasic } from 'pages/CaseManagementPage/CasesPage/helpers';

const LegalEntityCreateLicenceRenewPage = () => {
  const { t } = useTranslations();
  const { pathname, state: routerState } = useLocation();
  const [viewFormRenew, setViewFormRenew] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isFirearmDataLoading, setIsFirearmDataLoading] = useState(true);
  const [shouldPreserveWeapons, setShouldPreserveWeapons] = useState(false);
  const [hasHydratedWeapons, setHasHydratedWeapons] = useState(false);
  const [weaponSeedData, setWeaponSeedData] = useState<ILicenceFirearmsResponse[] | null>(
    null,
  );
  const [breadcrumbs, setBreadcrumbs] = useState<any[]>([]);
  const navigate = useNavigate();
  const query = useQuery();
  const { translations } = useLang();
  const {
    id = '',
    licenceId = '',
    caseId = '',
  } = useParams<{
    id: string;
    licenceId: string;
    caseId: string;
  }>();
  const type: string | null = query.get('type')?.trim() || null;
  const expand: string | null = query.get('expand');

  const { getLicenceData, licenceDetails } = useRenewLicence();

  const { getLicenceAttachmentsData, licenceAttachments, getLicenceWeaponData } =
    useLegalEntities();

  const isFromLE = useMemo(
    () =>
      pathname.indexOf(
        ERouteLinks.LegalEntityCreateLicenceRenewCase.replace(':id', id)
          .replace(':licenceId', licenceId)
          .replace(':caseId', caseId),
      ) !== -1,
    [pathname, id, licenceId, caseId],
  );

  useEffect(() => {
    (async function init() {
      if (licenceId) {
        setIsFirearmDataLoading(true);
        setWeaponSeedData(null);
        const currentLicenceId = LicenceStore.getValue('licenceId');
        const isReturningFromWeaponLinking = (routerState as any)?.saveToStory === true;
        const hasStoreData = currentLicenceId === licenceId;
        setHasHydratedWeapons(false);

        await getLicenceData(licenceId);
        await getLicenceAttachmentsData(licenceId);
        const weapons = await getLicenceWeaponData(licenceId);
        setWeaponSeedData(weapons);

        if (hasStoreData && isReturningFromWeaponLinking) {
          LicenceStore.setLicenceId(licenceId);
          setViewFormRenew(true);
          setShouldPreserveWeapons(true);
          setHasHydratedWeapons(true);
          setIsFirearmDataLoading(false);
        } else {
          LicenceStore.clearStore();
          LicenceStore.setLicenceId(licenceId);
          setShouldPreserveWeapons(false);
          setHasHydratedWeapons(false);
        }
      }
    })();
  }, [licenceId]);

  useEffect(() => {
    if (!viewFormRenew || !licenceId) {
      setIsFirearmDataLoading(false);
      return;
    }

    if (shouldPreserveWeapons) {
      setIsFirearmDataLoading(false);
      return;
    }

    if (weaponSeedData === null) {
      setIsFirearmDataLoading(true);
      return;
    }

    if (hasHydratedWeapons) {
      setIsFirearmDataLoading(false);
      return;
    }

    setIsFirearmDataLoading(true);

    inNextTick(() => {
      const firearmLinkToBeRemovedIds =
        LicenceStore.getFormFirearmLinkToBeRemovedIds(licenceId);
      const filteredWeapons = firearmLinkToBeRemovedIds.length
        ? weaponSeedData.filter((item) => !firearmLinkToBeRemovedIds.includes(item.uniqueId))
        : weaponSeedData;

      LicenceStore.setFormFirearmCurrentLink(filteredWeapons, licenceId);
      setHasHydratedWeapons(true);

      setIsFirearmDataLoading(false);
    });
  }, [viewFormRenew, licenceId, shouldPreserveWeapons, hasHydratedWeapons, weaponSeedData]);

  useEffect(() => {
    if (licenceAttachments?.content.length) {
      setIsLoading(true);

      inNextTick(() => {
        const existingAttachmentData = LicenceStore.getFormLicenceAttachmentsData(licenceId);
        if (!existingAttachmentData?.length) {
          const attachmentsToDeleteIds =
            LicenceStore.getFormLicenceAttachmentsToDeleteIds(licenceId);
          const filteredAttachments = attachmentsToDeleteIds.length
            ? licenceAttachments.content.filter(
                (item) => !attachmentsToDeleteIds.includes(item.uniqueId),
              )
            : licenceAttachments.content;

          LicenceStore.setFormLicenceAttachmentsData(filteredAttachments);
        }
        setIsLoading(false);
      });
    } else {
      setIsLoading(false);
    }
  }, [licenceAttachments, licenceId]);

  useEffect(() => {
    if (translations) {
      const baseBreadcrumbs = getBreadcrumbsBasic(isFromLE, type, t, false, id);
      setBreadcrumbs([
        ...baseBreadcrumbs,
        {
          label: t(Elabels.caseDetailsHeader),
          route: isFromLE
            ? `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`
            : `${ERouteLinks.CasesDetailsPage.replace(':caseId', caseId)}`,
        },
      ]);
    }
  }, [isFromLE, expand, id, licenceId, type, translations]);

  useEffect(() => {
    if (licenceDetails) {
      setViewFormRenew(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [licenceDetails]);

  const backLink = () => {
    LicenceStore.clearStore();
    if (isFromLE) {
      navigate(
        `${ERouteLinks.LegalEntityCasesDetailsPage.replace(':caseId', caseId).replace(':id', id)}?type=${type}&expand=cases`,
      );
    } else {
      navigate(ERouteLinks.CasesDetailsPage.replace(':caseId', caseId));
    }
  };

  const errorType = () => {
    if (isFromLE) {
      navigate(ERouteLinks.LegalEntities);
    } else {
      navigate(ERouteLinks.LicencesPage);
    }
  };

  return (
    <>
      <Breadcrumbs
        items={[...breadcrumbs, t('legalEntity.licenceDetails.renewLicence.header')]}
        onClickLink={() => LicenceStore.clearStore()}
      />
      <StyledPageTitle variant="h4">
        {t('legalEntity.licenceDetails.renewLicence.header')}
      </StyledPageTitle>
      {!isLoading && !isFirearmDataLoading && viewFormRenew && licenceDetails && (
        <LicenceRenewEdit
          id={licenceDetails.owner.legalUniqueId}
          caseId={caseId}
          licenceDetails={licenceDetails}
          licenceTemplateId={licenceDetails.weaponLicenceTemplateUniqueId}
          backLink={backLink}
          errorType={errorType}
          breadcrumbs={breadcrumbs}
        />
      )}
    </>
  );
};

export { LegalEntityCreateLicenceRenewPage };
